import React, { useState, useEffect } from 'react';
// import { DeleteButton } from './DeleteButton'; // Désactivé - utilisation directe
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  Plus, 
  BookOpen, 
  Users, 
  TrendingUp, 
  Loader2, 
  Crown, 
  Lock, 
  Upload,
  FileText,
  Video,
  PenTool,
  ExternalLink,
  Link,
  Play,
  Trash2
} from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  created_at: string;
}

interface CourseMaterial {
  id: string;
  course_id: string;
  title: string;
  content_type: 'course' | 'exercise' | 'video';
  html_content?: string;
  file_url?: string;
  is_membre_only: boolean;
  created_at: string;
}

interface DriveLink {
  id: string;
  course_id: string;
  title: string;
  description?: string;
  drive_url: string;
  is_membre_only: boolean;
  created_at: string;
}

interface PDFDocument {
  id: string;
  course_id: string;
  title: string;
  description?: string;
  file_url: string;
  file_size?: number;
  is_membre_only: boolean;
  created_at: string;
}

export const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseMaterials, setCourseMaterials] = useState<CourseMaterial[]>([]);
  const [driveLinks, setDriveLinks] = useState<DriveLink[]>([]);
  const [pdfDocuments, setPdfDocuments] = useState<PDFDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>('');

  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category: 'Mathématiques'
  });

  const [newDriveLink, setNewDriveLink] = useState({
    title: '',
    drive_url: ''
  });

  const [newPdfDocument, setNewPdfDocument] = useState({
    title: '',
    description: '',
    file: null as File | null
  });

  // Vérifications d'accès
  const isMembre = profile?.subscription_type === 'membre';
  const isStudent = profile?.role === 'student';
  const isTeacher = profile?.role === 'teacher';
  const isVisitor = profile?.subscription_type === 'visitor';

  // Fonction pour ouvrir le contenu HTML - VERSION CORRIGÉE
  const openHtmlContent = async (material: CourseMaterial) => {
    if (isStudent && isVisitor && material.is_membre_only) {
      toast({
        title: "🔒 Accès Membre requis",
        description: "Ce contenu est réservé aux membres Membre",
        variant: "destructive",
      });
      return;
    }

    let htmlContent = material.html_content || '';
    
    if (material.file_url && !htmlContent) {
      try {
        const response = await fetch(material.file_url);
        htmlContent = await response.text();
      } catch (error) {
        toast({
          title: "❌ Erreur",
          description: "Impossible de charger le fichier",
          variant: "destructive",
        });
        return;
      }
    }
    
    if (htmlContent) {
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [coursesResult, materialsResult, driveLinksResult, pdfDocumentsResult] = await Promise.all([
        supabase.from('courses_2025_12_02_23_42').select('*').order('created_at', { ascending: false }),
        supabase.from('course_materials_2025_12_03_23_06').select('*').order('created_at', { ascending: false }),
        supabase.from('drive_links_2025_12_10_10_05').select('*').order('created_at', { ascending: false }),
        supabase.from('pdf_documents_2025_12_10_13_35').select('*').order('created_at', { ascending: false })
      ]);

      setCourses(coursesResult.data || []);
      setCourseMaterials(materialsResult.data || []);
      setDriveLinks(driveLinksResult.data || []);
      setPdfDocuments(pdfDocumentsResult.data || []);
      
      console.log('Data loaded:', {
        courses: coursesResult.data?.length,
        materials: materialsResult.data?.length,
        driveLinks: driveLinksResult.data?.length
      });

    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: "Erreur de chargement",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourse.title.trim()) return;

    setCreating(true);
    try {
      const { error } = await supabase
        .from('courses_2025_12_02_23_42')
        .insert([newCourse]);

      if (error) throw error;

      toast({ title: "✅ Cours créé", description: "Le nouveau cours a été ajouté" });
      setNewCourse({ title: '', description: '', category: 'Mathématiques' });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const createDriveLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDriveLink.title.trim() || !newDriveLink.drive_url.trim() || !selectedCourse) return;

    setCreating(true);
    try {
      const { error } = await supabase
        .from('drive_links_2025_12_10_10_05')
        .insert([{
          course_id: selectedCourse,
          title: newDriveLink.title,
          drive_url: newDriveLink.drive_url,
          is_membre_only: true
        }]);

      if (error) throw error;

      toast({ title: "✅ Lien ajouté", description: "Le lien Google Drive a été ajouté" });
      setNewDriveLink({ title: '', drive_url: '' });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const uploadPdfDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPdfDocument.title.trim() || !newPdfDocument.file || !selectedCourse) return;

    setCreating(true);
    try {
      // Upload PDF to Supabase Storage
      const fileExt = newPdfDocument.file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `pdfs/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('course-files')
        .upload(filePath, newPdfDocument.file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('course-files')
        .getPublicUrl(filePath);

      // Save PDF info to database
      const { error } = await supabase
        .from('pdf_documents_2025_12_10_13_35')
        .insert([{
          course_id: selectedCourse,
          title: newPdfDocument.title,
          description: newPdfDocument.description,
          file_url: publicUrl,
          file_size: newPdfDocument.file.size,
          is_membre_only: true,
          uploaded_by: profile?.id
        }]);

      if (error) throw error;

      toast({ title: "✅ PDF ajouté", description: "Le document PDF a été téléchargé" });
      setNewPdfDocument({ title: '', description: '', file: null });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  // Fonctions de suppression pour les admins
  const deleteCourse = async (courseId: string) => {
    console.log('🗑️ Tentative suppression cours:', courseId);
    if (!window.confirm('Supprimer ce cours ?')) return;
    
    try {
      console.log('💾 Suppression en cours...');
      const { error } = await supabase
        .from('courses_2025_12_02_23_42')
        .delete()
        .eq('id', courseId);
      
      console.log('💾 Résultat suppression:', { error });
      if (error) throw error;
      
      toast({ title: "✅ Cours supprimé", description: "Le cours a été supprimé avec succès" });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deleteMaterial = async (materialId: string) => {
    if (!window.confirm('Supprimer ce matériel ?')) return;
    
    try {
      const { error } = await supabase
        .from('course_materials_2025_12_03_23_06')
        .delete()
        .eq('id', materialId);
      
      if (error) throw error;
      
      toast({ title: "✅ Matériel supprimé" });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deleteDriveLink = async (linkId: string) => {
    if (!window.confirm('Supprimer ce lien Drive ?')) return;
    
    try {
      const { error } = await supabase
        .from('drive_links_2025_12_10_10_05')
        .delete()
        .eq('id', linkId);
      
      if (error) throw error;
      
      toast({ title: "✅ Lien supprimé" });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deletePdfDocument = async (pdfId: string) => {
    if (!confirm('Supprimer ce document PDF ?')) return;
    
    try {
      const { error } = await supabase
        .from('pdf_documents_2025_12_10_13_35')
        .delete()
        .eq('id', pdfId);
      
      if (error) throw error;
      
      toast({ title: "✅ PDF supprimé" });
      loadData();
    } catch (error: any) {
      toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirection pour étudiants visiteurs
  if (isStudent && isVisitor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-secondary mb-4">🔒 Accès Membre Requis</h1>
          <p className="text-xl text-muted-foreground mb-8">
            Vous êtes un <strong>étudiant visiteur</strong>. Pour accéder aux cours, passez Membre.
          </p>
          <Card className="sprintbac-glow border-0 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="text-5xl font-bold text-secondary mb-2">
                300 DH<span className="text-lg text-muted-foreground font-normal">/mois</span>
              </div>
              <p className="text-muted-foreground mb-6">Accès complet à tous les contenus</p>
              <div className="space-y-3 mb-6 text-left max-w-md mx-auto">
                <div className="flex items-center gap-3">
                  <Video className="h-5 w-5 text-primary" />
                  <span>Vidéos Google Drive HD</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <span>Cours HTML et exercices</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  <span>Chat avec Prof IA 24h/7j</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-secondary mb-2">
          Bonjour {profile?.name} 👋
        </h1>
        <div className="flex items-center justify-center gap-4 text-lg">
          <Badge variant="outline" className="text-base px-4 py-2">
            {isTeacher ? '👨‍🏫 Enseignant' : '👨‍🎓 Étudiant'}
          </Badge>
          <Badge className={isMembre ? 'sprintbac-gradient text-white text-base px-4 py-2' : 'bg-gray-200 text-gray-800 text-base px-4 py-2'}>
            {isMembre ? (
              <>
                <Crown className="mr-2 h-4 w-4" />
                Membre
              </>
            ) : (
              '🔒 Visiteur'
            )}
          </Badge>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <BookOpen className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{courses.length}</div>
            <p className="text-sm text-muted-foreground">Cours</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <FileText className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{courseMaterials.length}</div>
            <p className="text-sm text-muted-foreground">Matériels HTML</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <Play className="h-8 w-8 text-primary mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{driveLinks.length}</div>
            <p className="text-sm text-muted-foreground">Vidéos Drive</p>
          </CardContent>
        </Card>
        <Card className="border-0 sprintbac-shadow">
          <CardContent className="pt-6 text-center">
            <FileText className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary">{pdfDocuments.length}</div>
            <p className="text-sm text-muted-foreground">Documents PDF</p>
          </CardContent>
        </Card>
      </div>

      {/* Debug Section - Google Drive Links */}
      {isTeacher && (
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800">🔧 Debug - Liens Google Drive</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-orange-700">
              <p><strong>Total liens Drive:</strong> {driveLinks.length}</p>
              {driveLinks.length > 0 && (
                <div className="mt-2">
                  <p><strong>Liste des liens:</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    {driveLinks.map((link) => (
                      <li key={link.id}>
                        <strong>{link.title}</strong> (Cours: {link.course_id}) - 
                        <a href={link.drive_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                          {link.drive_url.substring(0, 50)}...
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Section - Only for Teachers */}
      {isTeacher && (
        <>
          {/* Create Course */}
          <Card className="border-0 sprintbac-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-secondary">
                <Plus className="h-5 w-5" />
                Créer un cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createCourse} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Titre du cours"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    required
                  />
                  <Select 
                    value={newCourse.category} 
                    onValueChange={(value) => setNewCourse({ ...newCourse, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mathématiques">Mathématiques</SelectItem>
                      <SelectItem value="Sciences">Sciences</SelectItem>
                      <SelectItem value="Informatique">Informatique</SelectItem>
                      <SelectItem value="Langues">Langues</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Description du cours"
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  rows={2}
                />
                <Button 
                  type="submit" 
                  className="sprintbac-gradient hover:opacity-90"
                  disabled={creating}
                >
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Créer le cours
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Add Google Drive Link - ULTRA SIMPLE */}
          <Card className="border-0 sprintbac-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-secondary">
                <Play className="h-5 w-5" />
                Ajouter une vidéo Google Drive
              </CardTitle>
              <CardDescription>
                Ajoutez des liens vers vos vidéos Google Drive pour les étudiants membre
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createDriveLink} className="space-y-4">
                <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un cours" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Input
                  placeholder="Titre de la vidéo"
                  value={newDriveLink.title}
                  onChange={(e) => setNewDriveLink({ ...newDriveLink, title: e.target.value })}
                  required
                />
                
                <Input
                  placeholder="https://drive.google.com/file/d/..."
                  value={newDriveLink.drive_url}
                  onChange={(e) => setNewDriveLink({ ...newDriveLink, drive_url: e.target.value })}
                  required
                />
                
                <Button 
                  type="submit" 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={creating || !selectedCourse}
                >
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                  Ajouter la vidéo Drive
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Add PDF Document */}
          <Card className="border-0 sprintbac-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-secondary">
                <FileText className="h-5 w-5" />
                Ajouter un document PDF
              </CardTitle>
              <CardDescription>
                Téléchargez des documents PDF pour les étudiants membre
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={uploadPdfDocument} className="space-y-4">
                <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un cours" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Input
                  placeholder="Titre du document"
                  value={newPdfDocument.title}
                  onChange={(e) => setNewPdfDocument({ ...newPdfDocument, title: e.target.value })}
                  required
                />
                
                <Textarea
                  placeholder="Description du document (optionnel)"
                  value={newPdfDocument.description}
                  onChange={(e) => setNewPdfDocument({ ...newPdfDocument, description: e.target.value })}
                  rows={2}
                />
                
                <div>
                  <label className="block text-sm font-medium mb-2">Fichier PDF</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setNewPdfDocument({ ...newPdfDocument, file });
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={creating || !selectedCourse || !newPdfDocument.file}
                >
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  Télécharger le PDF
                </Button>
              </form>
            </CardContent>
          </Card>
        </>
      )}

      {/* Courses Section */}
      <Card className="border-0 sprintbac-shadow">
        <CardHeader>
          <CardTitle className="text-secondary">Mes cours</CardTitle>
          <CardDescription>
            {courses.length === 0 ? "Aucun cours disponible" : `${courses.length} cours disponible(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun cours disponible pour le moment</p>
            </div>
          ) : (
            <div className="space-y-6">
              {courses.map((course) => {
                const materials = courseMaterials.filter(m => m.course_id === course.id);
                const courseLinks = driveLinks.filter(link => link.course_id === course.id);
                const coursePdfs = pdfDocuments.filter(pdf => pdf.course_id === course.id);
                
                console.log(`Course ${course.title}:`, {
                  courseId: course.id,
                  materialsCount: materials.length,
                  driveLinksCount: courseLinks.length,
                  driveLinks: courseLinks
                });
                
                return (
                  <Card key={course.id} className="border-0 bg-gradient-to-br from-white to-blue-50">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg text-secondary">
                            {course.title}
                          </CardTitle>
                          <Badge className="bg-blue-100 text-blue-800 mt-2">
                            {course.category}
                          </Badge>
                        </div>
                        {isTeacher && (
                          <Button
                            size="sm"
                            type="button"
                            onClick={async () => {
                              console.log('Suppression cours:', course.id);
                              await deleteCourse(course.id);
                            }}
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            🗑️ Supprimer cours
                          </Button>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {course.description || "Aucune description"}
                      </p>
                    </CardHeader>
                    <CardContent>
                      {/* HTML Materials */}
                      {materials.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium text-sm text-secondary mb-3">📄 Contenu HTML :</h4>
                          <div className="space-y-2">
                            {materials.map((material) => {
                              const canAccess = isMembre || !material.is_membre_only || isTeacher;
                              
                              return (
                                <div key={material.id} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm">{material.title}</span>
                                    {material.is_membre_only && <Crown className="h-3 w-3 text-yellow-500" />}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => openHtmlContent(material)}
                                      disabled={!canAccess}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      {canAccess ? 'Ouvrir' : '🔒 Membre'}
                                    </Button>
                                    {isTeacher && (
                                      <button
                                        onClick={async () => {
                                          console.log('SUPPRESSION MATERIEL:', material.id);
                                          if (window.confirm('Supprimer ce matériel ?')) {
                                            try {
                                              const { error } = await supabase
                                                .from('course_materials_2025_12_03_23_06')
                                                .delete()
                                                .eq('id', material.id);
                                              if (error) throw error;
                                              console.log('MATÉRIEL SUPPRIMÉ');
                                              await loadData();
                                              alert('Matériel supprimé avec succès !');
                                            } catch (err) {
                                              console.error('Erreur suppression:', err);
                                              alert('Erreur: ' + err.message);
                                            }
                                          }
                                        }}
                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold"
                                        type="button"
                                      >
                                        DEL
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Google Drive Links - SIMPLE ET FONCTIONNEL */}
                      {courseLinks.length > 0 ? (
                        <div className="mb-4">
                          <h4 className="font-medium text-sm text-secondary mb-3">🎥 Vidéos Google Drive :</h4>
                          <div className="space-y-2">
                            {courseLinks.map((link) => {
                              const canAccess = isMembre || !link.is_membre_only || isTeacher;
                              
                              return (
                                <div key={link.id} className="flex items-center justify-between p-2 bg-green-50 rounded">
                                  <div className="flex items-center gap-2">
                                    <Play className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-medium">{link.title}</span>
                                    <Crown className="h-3 w-3 text-yellow-500" />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        console.log('Opening Drive link:', link.drive_url);
                                        window.open(link.drive_url, '_blank');
                                      }}
                                      disabled={!canAccess}
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                    >
                                      {canAccess ? 'Regarder' : '🔒 Membre'}
                                    </Button>
                                    {isTeacher && (
                                      <Button
                                        size="sm"
                                        onClick={() => deleteDriveLink(link.id)}
                                        variant="destructive"
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        🗑️
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-sm text-yellow-800">
                            🎥 <strong>Aucune vidéo Google Drive</strong> pour ce cours.
                            {isTeacher && (
                              <span className="block mt-1">
                                💡 Utilisez le formulaire ci-dessus pour ajouter des vidéos.
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                      
                      {/* PDF Documents */}
                      {coursePdfs.length > 0 ? (
                        <div className="mb-4">
                          <h4 className="font-medium text-sm text-secondary mb-3">📄 Documents PDF :</h4>
                          <div className="space-y-2">
                            {coursePdfs.map((pdf) => {
                              const canAccess = isMembre || !pdf.is_membre_only || isTeacher;
                              
                              return (
                                <div key={pdf.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-red-600" />
                                    <div>
                                      <span className="text-sm font-medium">{pdf.title}</span>
                                      {pdf.description && (
                                        <p className="text-xs text-gray-600">{pdf.description}</p>
                                      )}
                                    </div>
                                    <Crown className="h-3 w-3 text-yellow-500" />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        console.log('Opening PDF:', pdf.file_url);
                                        window.open(pdf.file_url, '_blank');
                                      }}
                                      disabled={!canAccess}
                                      className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                      {canAccess ? 'Télécharger' : '🔒 Membre'}
                                    </Button>
                                    {isTeacher && (
                                      <Button
                                        size="sm"
                                        onClick={() => deletePdfDocument(pdf.id)}
                                        variant="destructive"
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        🗑️
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
                          <p className="text-sm text-red-800">
                            📄 <strong>Aucun document PDF</strong> pour ce cours.
                            {isTeacher && (
                              <span className="block mt-1">
                                💡 Utilisez le formulaire ci-dessus pour ajouter des PDF.
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                      
                      {(materials.length === 0 && courseLinks.length === 0 && coursePdfs.length === 0) && (
                        <p className="text-muted-foreground text-sm">Aucun contenu disponible</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};